import type { ClassifyChecklistParams, ClassifyChecklistState, Classification } from '../types';

interface Props {
  params: ClassifyChecklistParams;
  puzzleState: ClassifyChecklistState;
  onSolve: () => void;
  patchState: (updater: (prev: ClassifyChecklistState) => ClassifyChecklistState) => void;
}

export function ClassifyChecklist({ params, puzzleState, onSolve, patchState }: Props) {
  const { items } = params;
  const { classified, solved } = puzzleState;

  function classify(id: string, type: Classification) {
    patchState(s => {
      const next = { ...s.classified, [id]: type };
      const allCorrect = items.every(item => {
        const c = next[item.id];
        return item.isMeasurement ? c === 'measurement' : c === 'reality';
      });
      if (allCorrect) {
        onSolve();
        return { ...s, classified: next, solved: true };
      }
      return { ...s, classified: next };
    });
  }

  if (solved) {
    return (
      <p className="puzzle-solved-msg">
        <i className="fa-solid fa-circle-check" aria-hidden="true" /> All items classified correctly.
      </p>
    );
  }

  const correctCount = items.filter(item => {
    const c = classified[item.id];
    return item.isMeasurement ? c === 'measurement' : c === 'reality';
  }).length;

  return (
    <div className="puzzle-classify">
      <p className="puzzle-narrative">
        For each object, decide: is it a direct reality, or a measurement of reality?
      </p>
      <p className="puzzle-classify__progress">
        {correctCount} / {items.length} correctly classified
      </p>
      <ul className="puzzle-classify__list">
        {items.map(item => {
          const c = classified[item.id];
          const isCorrect = c !== undefined && (item.isMeasurement ? c === 'measurement' : c === 'reality');
          return (
            <li key={item.id} className={`puzzle-classify__item${c ? ' puzzle-classify__item--classified' : ''}`}>
              <div className="puzzle-classify__item-header">
                <span className="puzzle-classify__item-label">{item.label}</span>
                {c && (
                  <span className={`puzzle-classify__badge puzzle-classify__badge--${c}${isCorrect ? ' puzzle-classify__badge--correct' : ' puzzle-classify__badge--wrong'}`}>
                    {isCorrect
                      ? <i className="fa-solid fa-check" aria-hidden="true" />
                      : <i className="fa-solid fa-xmark" aria-hidden="true" />}
                    {' '}{c}
                  </span>
                )}
              </div>
              <p className="puzzle-classify__item-desc">{item.description}</p>
              <div className="puzzle-classify__btns">
                <button
                  className={`escape-classify-btn escape-classify-btn--reality${c === 'reality' ? ' escape-classify-btn--active' : ''}`}
                  type="button"
                  onClick={() => classify(item.id, 'reality')}
                >
                  <i className="fa-solid fa-eye" aria-hidden="true" /> Reality
                </button>
                <button
                  className={`escape-classify-btn escape-classify-btn--measurement${c === 'measurement' ? ' escape-classify-btn--active' : ''}`}
                  type="button"
                  onClick={() => classify(item.id, 'measurement')}
                >
                  <i className="fa-solid fa-chart-line" aria-hidden="true" /> Measurement
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
